import "../promoterList/style.css";
import "../promoterList/competiterList.css";
import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  Dropdown,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Table,
  Tag,
  Tooltip,
  Upload,
} from "antd";
import moment from "moment";
import {
  DeleteOutlined,
  LoadingOutlined,
  MoreOutlined,
  PaperClipOutlined,
  PlusOutlined,
  SearchOutlined,
  StarFilled,
  StarOutlined,
} from "@ant-design/icons";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  updateDoc,
} from "firebase/firestore";
import { deleteObject, ref } from "firebase/storage";
import { useSelector } from "react-redux";
import { db, storage } from "../../firebase";
import { uploadFile } from "../../helper/firebaseUpload";
import { GreenNotify, RedNotify } from "../../helper/helper";
import AddressAutocomplete from "../../components/addressAutocomplete/AddressAutocomplete";
import Loader from "../../components/loader/loader";
import PageHeader from "../../components/pageHeader/PageHeader";

const COLLECTION = "vendors";

// Best-effort delete of a Storage object by its download URL. Swallows errors
// (already-gone / cross-bucket / permissions) — orphan cleanup must never block
// or fail a Firestore write that already succeeded.
const deleteStorageFile = async (url) => {
  if (!url) return;
  try {
    await deleteObject(ref(storage, url));
  } catch (error) {
    console.warn("[vendors] storage cleanup skipped:", error?.message || error);
  }
};

// Curated trade categories. Admins maintain the directory, so a fixed list keeps
// the data clean and drives the mobile filter; extend here if a new trade is needed.
export const VENDOR_CATEGORIES = [
  "Plumbing",
  "Electrical",
  "HVAC",
  "Landscaping",
  "Cleaning",
  "Roofing",
  "Pest Control",
  "General Contractor",
  "Appliance Repair",
  "Other",
];

const { TextArea } = Input;

const Vendors = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  // Logo + documents are uploaded to Storage on pick (async), so they live in
  // component state rather than the antd Form value tree.
  const [logoUrl, setLogoUrl] = useState("");
  const [documents, setDocuments] = useState([]); // [{ name, url }]
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  // Lat/lng captured from the address autocomplete (mirrors how properties store
  // coordinates) so the mobile app can offer turn-by-turn directions, not just a
  // map search on the address string.
  const [coordinates, setCoordinates] = useState(null);
  const [form] = Form.useForm();

  const adminUser = useSelector((state) => state.userDataSlice?.userData);

  // Realtime directory — mirrors the Properties list (onSnapshot keeps the table
  // live as other admins add/edit vendors).
  useEffect(() => {
    setLoading(true);
    const unsub = onSnapshot(
      collection(db, COLLECTION),
      (snap) => {
        setVendors(snap.docs.map((d) => ({ _id: d.id, ...d.data() })));
        setLoading(false);
      },
      (error) => {
        console.error("Error loading vendors:", error);
        RedNotify("Failed to load vendors");
        setLoading(false);
      },
    );
    return () => unsub();
  }, []);

  const resetState = () => {
    setModalOpen(false);
    setEditing(null);
    setLogoUrl("");
    setDocuments([]);
    setCoordinates(null);
    form.resetFields();
  };

  const openCreate = () => {
    setEditing(null);
    setLogoUrl("");
    setDocuments([]);
    setCoordinates(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (vendor) => {
    setEditing(vendor);
    setLogoUrl(vendor?.logoUrl || "");
    setDocuments(Array.isArray(vendor?.documents) ? vendor.documents : []);
    setCoordinates(vendor?.coordinates || null);
    form.setFieldsValue({
      name: vendor?.name || "",
      category: vendor?.category || undefined,
      phone: vendor?.phone || "",
      email: vendor?.email || "",
      website: vendor?.website || "",
      address: vendor?.address || "",
      contactPerson: vendor?.contactPerson || "",
      notes: vendor?.notes || "",
    });
    setModalOpen(true);
  };

  // antd Upload calls this per picked file; we upload to Storage ourselves and
  // return false so antd doesn't try its own XHR upload.
  const handleLogoPick = async (file) => {
    setUploadingLogo(true);
    try {
      const url = await uploadFile(file, "vendor-logos");
      if (!url) throw new Error("Logo upload failed");
      setLogoUrl(url);
    } catch (error) {
      RedNotify(error?.message || "Logo upload failed");
    } finally {
      setUploadingLogo(false);
    }
    return false;
  };

  const handleDocPick = async (file) => {
    setUploadingDoc(true);
    try {
      const url = await uploadFile(file, "vendor-documents");
      if (!url) throw new Error("Document upload failed");
      setDocuments((prev) => [...prev, { name: file.name || "Document", url }]);
    } catch (error) {
      RedNotify(error?.message || "Document upload failed");
    } finally {
      setUploadingDoc(false);
    }
    return false;
  };

  const removeDocument = (url) =>
    setDocuments((prev) => prev.filter((d) => d.url !== url));

  const onFinish = async (values) => {
    setSaving(true);
    try {
      const nowIso = new Date().toISOString();
      const payload = {
        name: (values.name || "").trim(),
        category: values.category || "",
        phone: (values.phone || "").trim(),
        email: (values.email || "").trim(),
        website: (values.website || "").trim(),
        address: (values.address || "").trim(),
        contactPerson: (values.contactPerson || "").trim(),
        notes: (values.notes || "").trim(),
        // `preferred` is intentionally NOT in this shared payload — it's toggled
        // inline from the table, so an edit-save must not clobber that value.
        // Coordinates only stay meaningful while they match the typed address;
        // the autocomplete clears them on a manual edit (see AddressAutocomplete).
        coordinates: (values.address || "").trim() ? coordinates : null,
        logoUrl: logoUrl || "",
        documents,
        isActive: true,
        updatedAt: nowIso,
        updatedBy: adminUser?.uuid || adminUser?._id || "",
      };

      if (editing?._id) {
        await updateDoc(doc(db, COLLECTION, editing._id), payload);
        GreenNotify("Vendor updated successfully");
        // Now that the write committed, drop any Storage files this edit
        // replaced/removed (the logo, and any detached documents). Done here —
        // not on pick/remove — so cancelling the modal never orphans a file
        // that the saved doc still references.
        if (editing.logoUrl && editing.logoUrl !== logoUrl) {
          deleteStorageFile(editing.logoUrl);
        }
        const keptUrls = new Set(documents.map((d) => d.url));
        (editing.documents || []).forEach((d) => {
          if (d?.url && !keptUrls.has(d.url)) deleteStorageFile(d.url);
        });
      } else {
        await addDoc(collection(db, COLLECTION), {
          ...payload,
          preferred: false, // new vendors start un-preferred; set it from the table
          createdAt: nowIso,
          createdBy: adminUser?.uuid || adminUser?._id || "",
          createdByName: adminUser?.name || "",
        });
        GreenNotify("Vendor created successfully");
      }
      resetState();
    } catch (error) {
      console.error("Error saving vendor:", error);
      RedNotify(error?.message || "Vendor save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (vendor) => {
    Modal.confirm({
      title: "Confirm Delete",
      content: `Delete vendor "${vendor?.name}"? Tasks already linked to it keep the vendor's name.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await deleteDoc(doc(db, COLLECTION, vendor._id));
          GreenNotify("Vendor deleted");
          // Hard delete → its Storage files are now orphaned; remove them.
          deleteStorageFile(vendor.logoUrl);
          (vendor.documents || []).forEach((d) => deleteStorageFile(d?.url));
        } catch (error) {
          RedNotify(error?.message || "Delete failed");
        }
      },
    });
  };

  // Preferred is a quick inline flag — toggled straight from the table (the
  // realtime listener reflects it), not buried in the create/edit form.
  const togglePreferred = async (vendor) => {
    try {
      await updateDoc(doc(db, COLLECTION, vendor._id), {
        preferred: !vendor.preferred,
        updatedAt: new Date().toISOString(),
        updatedBy: adminUser?.uuid || adminUser?._id || "",
      });
    } catch (error) {
      RedNotify(error?.message || "Couldn't update preferred");
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      align: "center",
      sorter: (a, b) => a.rawName.localeCompare(b.rawName),
    },
    {
      title: "Preferred",
      dataIndex: "preferred",
      align: "center",
      // Preferred first so the starred vendors group together.
      sorter: (a, b) => (b.preferredValue ? 1 : 0) - (a.preferredValue ? 1 : 0),
    },
    { title: "Category", dataIndex: "category", align: "center" },
    { title: "Phone", dataIndex: "phone", align: "center" },
    { title: "Contact", dataIndex: "contactPerson", align: "center" },
    { title: "Added", dataIndex: "created", align: "center" },
    { title: "Actions", dataIndex: "actions", align: "center" },
  ];

  const data = useMemo(
    () =>
      vendors
        .map((v) => ({
          key: v._id,
          rawName: v?.name?.toLowerCase() || "",
          rawCategory: v?.category || "",
          preferredValue: !!v?.preferred,
          name: (
            <div className="task-link" onClick={() => openEdit(v)}>
              {v?.name || "Untitled"}
            </div>
          ),
          preferred: (
            <Tooltip
              title={
                v?.preferred
                  ? "Preferred vendor — click to unset"
                  : "Mark as preferred"
              }
            >
              <Button
                type="text"
                aria-label="Toggle preferred vendor"
                icon={
                  v?.preferred ? (
                    <StarFilled style={{ color: "#f5a623" }} />
                  ) : (
                    <StarOutlined style={{ color: "#bbb" }} />
                  )
                }
                onClick={() => togglePreferred(v)}
              />
            </Tooltip>
          ),
          category: v?.category ? <Tag>{v.category}</Tag> : "—",
          phone: v?.phone || "—",
          contactPerson: v?.contactPerson || "—",
          created: v?.createdAt
            ? moment(v.createdAt).format("MM-DD-YYYY")
            : "—",
          actions: (
            <Dropdown
              trigger={["click"]}
              menu={{
                items: [
                  { key: "edit", label: "Edit", onClick: () => openEdit(v) },
                  {
                    key: "delete",
                    label: "Delete",
                    danger: true,
                    onClick: () => handleDelete(v),
                  },
                ],
              }}
            >
              <Button type="text" icon={<MoreOutlined />} />
            </Dropdown>
          ),
        }))
        .filter(
          (row) =>
            row.rawName.includes(searchText?.toLowerCase() || "") &&
            (!categoryFilter || row.rawCategory === categoryFilter),
        ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vendors, searchText, categoryFilter],
  );

  const getRowClassName = (record, index) =>
    index % 2 === 0 ? "server-role-even-row" : "server-role-odd-row";

  return (
    <div className="admin-products-main-container">
      <Loader loading={loading} />
      <PageHeader
        eyebrow="Operations"
        title="Vendors"
        subtitle="Maintain a directory of service vendors employees can reference and attach to tasks."
        actions={
          <>
            <Input
              className="sh-search"
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Search vendors"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <Select
              allowClear
              placeholder="All categories"
              style={{ minWidth: 170 }}
              value={categoryFilter}
              onChange={(val) => setCategoryFilter(val || null)}
              options={VENDOR_CATEGORIES.map((c) => ({ value: c, label: c }))}
            />
            <Button
              type="primary"
              className="sh-add-btn"
              icon={<PlusOutlined />}
              onClick={openCreate}
            >
              New vendor
            </Button>
          </>
        }
      />

      <div className="server-roles-tb-main-container">
        <Table
          rowClassName={getRowClassName}
          rowKey="key"
          columns={columns}
          dataSource={data}
          pagination={true}
          className="subscriptionapi-table"
        />
      </div>

      <Modal
        open={modalOpen}
        title={editing ? "Edit vendor" : "New vendor"}
        onCancel={resetState}
        onOk={() => form.submit()}
        okText={editing ? "Save changes" : "Create vendor"}
        confirmLoading={saving}
        destroyOnClose
        width={640}
        style={{ top: 24 }}
        styles={{ body: { maxHeight: "72vh", overflowY: "auto" } }}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          {/* Identity */}
          <Row gutter={16}>
            <Col xs={24} sm={15}>
              <Form.Item
                name="name"
                label="Name"
                rules={[{ required: true, message: "Vendor name is required" }]}
              >
                <Input placeholder="e.g. Acme Plumbing" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={9}>
              <Form.Item name="category" label="Category">
                <Select
                  showSearch
                  allowClear
                  placeholder="Select a trade"
                  options={VENDOR_CATEGORIES.map((c) => ({ value: c, label: c }))}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Contact */}
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="phone" label="Phone">
                <Input placeholder="(555) 123-4567" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[{ type: "email", message: "Enter a valid email" }]}
              >
                <Input placeholder="contact@vendor.com" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="website" label="Website">
                <Input placeholder="https://vendor.com" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="contactPerson" label="Contact person">
                <Input placeholder="Jane Doe" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="address" label="Address">
            <AddressAutocomplete
              placeholder="Start typing an address…"
              onPlaceSelected={({ coordinates: c }) => setCoordinates(c)}
            />
          </Form.Item>

          <Form.Item name="notes" label="Notes">
            <TextArea
              autoSize={{ minRows: 2, maxRows: 4 }}
              placeholder="Anything employees should know"
            />
          </Form.Item>

          <Form.Item label="Logo">
            <Upload
              listType="picture-card"
              showUploadList={false}
              accept="image/*"
              beforeUpload={handleLogoPick}
            >
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="logo"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <div>
                  {uploadingLogo ? <LoadingOutlined /> : <PlusOutlined />}
                  <div style={{ marginTop: 4, fontSize: 12 }}>Logo</div>
                </div>
              )}
            </Upload>
          </Form.Item>

          <Form.Item label="Documents">
            <Upload
              multiple
              showUploadList={false}
              beforeUpload={handleDocPick}
            >
              <Button icon={<PaperClipOutlined />} loading={uploadingDoc}>
                Attach document
              </Button>
            </Upload>
            {documents.length > 0 && (
              <div style={{ marginTop: 12 }}>
                {documents.map((docItem) => (
                  <div
                    key={docItem.url}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "4px 0",
                    }}
                  >
                    <a href={docItem.url} target="_blank" rel="noreferrer">
                      {docItem.name}
                    </a>
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => removeDocument(docItem.url)}
                    />
                  </div>
                ))}
              </div>
            )}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Vendors;
